import type {
  ConfigurableProperty,
  DigitalTwinSettingCreationRequest,
  DigitalTwinSettingTarget,
} from 'api/digitalTwin/digitalTwin.api'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import Datetime from 'utils/datetime/datetime'
import type { CreateSettingData } from '../DigitalTwinSettingDialogContent/DigitalTwinSettingDialogContent'

type NewPropertySettingParams = {
  property: DigitalTwinSettingTarget
  isTimeSeries: boolean
  isBaseSettings: boolean
  priorities: SystemSettingPriorityLevel[]
  /** The model's configurable properties, which say what priorities each property accepts. */
  configurableProperties?: ConfigurableProperty[]
  isCollectedSettings?: boolean
  period?: Period
}

/**
 * The priority a setting on a property is saved with: the current one if both the view and the
 * property's model allow it, otherwise the first view priority the model allows. The backend rejects
 * a priority the model does not list, and the view's dropdown cannot show one it does not offer.
 */
export function getPriorityAllowedForProperty({
  currentPriority,
  availablePriorities,
  priorities,
}: {
  currentPriority?: number
  availablePriorities?: number[]
  priorities: SystemSettingPriorityLevel[]
}): number | undefined {
  const allowed = availablePriorities?.length
    ? priorities.filter((priority) => availablePriorities.includes(priority.priority_level))
    : priorities
  if (allowed.some((priority) => priority.priority_level === currentPriority)) {
    return currentPriority
  }
  return allowed[0]?.priority_level ?? priorities[0]?.priority_level ?? currentPriority
}

/**
 * The levels a setting may be saved on. Deviation settings also lists the system's default levels so
 * their settings can be edited, but only Collected settings may create one, so a new or copied
 * setting is offered every level except those.
 */
export function getPrioritiesForSetting({
  priorities,
  isUpdate,
  isCollectedSettings,
}: {
  priorities: SystemSettingPriorityLevel[]
  isUpdate: boolean
  isCollectedSettings: boolean
}): SystemSettingPriorityLevel[] {
  return isUpdate || isCollectedSettings ? priorities : priorities.filter((priority) => !priority.is_default)
}

/** The deviation types the form offers: the levels a setting may be saved on that the property's model accepts. */
export function getSelectablePriorities({
  availablePriorities,
  ...settingPriorities
}: Parameters<typeof getPrioritiesForSetting>[0] & { availablePriorities?: number[] }): SystemSettingPriorityLevel[] {
  const offered = getPrioritiesForSetting(settingPriorities)
  return availablePriorities?.length
    ? offered.filter((priority) => availablePriorities.includes(priority.priority_level))
    : offered
}

/**
 * A new setting on one property. It covers the selected period where there is one; otherwise a base
 * setting starts this hour and runs until further notice, and any other setting covers today.
 */
export function getNewPropertySetting({
  property,
  isTimeSeries,
  isBaseSettings,
  priorities,
  configurableProperties,
  isCollectedSettings = false,
  period,
}: NewPropertySettingParams): CreateSettingData {
  const now = Datetime.getISONow()

  let startTime: ISODateTime
  if (period?.startTime) {
    startTime = Datetime.toISOString(period.startTime)
  } else if (isBaseSettings) {
    startTime = now
  } else {
    startTime = Datetime.getStartOfDay(now)
  }

  let endTime: ISODateTime | null = null
  if (period?.endTime) {
    endTime = Datetime.toISOString(period.endTime)
  } else if (!isBaseSettings) {
    endTime = Datetime.getEndOfDay(now)
  }

  return {
    level: property.level,
    name: property.name,
    attribute: property.attribute,
    start_time: startTime,
    end_time: endTime,
    comment: '',
    value: isTimeSeries ? [] : undefined,
    priority: getPriorityAllowedForProperty({
      availablePriorities: configurableProperties?.find(
        (p) => p.name === property.name && p.attribute === property.attribute
      )?.available_priorities,
      priorities: getPrioritiesForSetting({ priorities, isUpdate: false, isCollectedSettings }),
    }),
    isTimeSeries,
  }
}

export function toEditableSetting(setting: DigitalTwinSetting): CreateSettingData {
  const value = typeof setting.value === 'string' ? Number(setting.value) : setting.value
  return {
    ...setting,
    value,
    operational_event: setting.operational_event ?? null,
  }
}

/** The property a setting is on, or null while the user has not chosen one in the form yet. */
export function getSettingTarget(
  setting: CreateSettingData | DigitalTwinSettingCreationRequest
): DigitalTwinSettingTarget | null {
  if (!setting.level || !setting.name || !setting.attribute) {
    return null
  }
  return { level: setting.level, name: setting.name, attribute: setting.attribute }
}

/**
 * The priority a setting opens with. A saved setting keeps its own; a new or copied one is moved to a
 * level a new setting may use, since a copy carries the priority of the setting it came from.
 */
export function getOpenedSettingPriority({
  setting,
  priorities,
  configurableProperties,
  isCollectedSettings,
}: {
  setting: Pick<CreateSettingData, 'id' | 'name' | 'attribute' | 'priority'>
  priorities: SystemSettingPriorityLevel[]
  configurableProperties: ConfigurableProperty[]
  isCollectedSettings: boolean
}): number {
  const isUpdate = setting.id !== undefined
  if (isUpdate && setting.priority !== undefined) {
    return setting.priority
  }
  return (
    getPriorityAllowedForProperty({
      currentPriority: setting.priority,
      availablePriorities: configurableProperties.find(
        (p) => p.name === setting.name && p.attribute === setting.attribute
      )?.available_priorities,
      priorities: getPrioritiesForSetting({ priorities, isUpdate, isCollectedSettings }),
    }) ?? priorities[0].priority_level
  )
}
